import { Theme, ColorMode } from 'theme-ui';
import baseTheme from '@codersclan/gatsby-theme-airfleet/src/gatsby-plugin-theme-ui';
import merge from 'lodash.merge';

import { fontSizes, lineHeights } from './definitions';

declare global {
  interface ColorOpacity {
    100: string;
    90?: string;
    80?: string;
    70?: string;
    60?: string;
    50?: string;
    40?: string;
    30?: string;
    20?: string;
    10?: string;
    5?: string;
  }
  interface ThemeKenna extends Theme {
    colors?: ColorMode & {
      /**
       * Nested color modes can provide overrides when used in conjunction with
       * `Theme.initialColorModeName and `useColorMode()`
       */
      modes?: {
        [k: string]: ColorMode;
      };
      teal: ColorOpacity;
      violet: ColorOpacity;
      lavender: ColorOpacity;
      sungold: ColorOpacity;
      indigo: ColorOpacity;
      black: ColorOpacity;
      grayDark: ColorOpacity;
      grayMedium: ColorOpacity;
      grayLight: ColorOpacity;
    };
    fonts: { [k: string]: string };
  }
}

function reduceArray(arr: string[]): { [k: number]: number } {
  return arr.reduce(
    (prev, current, idx) => ({ ...prev, [parseInt(current, 10)]: idx }),
    {},
  );
}

const fontSizePosition = reduceArray(fontSizes);
const lineHeightPosition = reduceArray(lineHeights);

const theme: ThemeKenna = merge({}, baseTheme, {
  fonts: {
    robotoSlab: '"Roboto Slab", serif',
    lato: '"Lato", sans-serif',
    body: '"Lato", sans-serif',
  },
  fontSizes: fontSizes,
  lineHeights: lineHeights,
  letterSpacings: [
    '0px', // 0
    '0.1px', // 1
    '0.2px', // 2
    '0.5px', // 3
    '0.6px', // 4
  ],
  colors: {
    /**
     * HEX: #32CDEA
     */
    teal: {
      100: 'rgb(50, 205, 234)',
      90: 'rgba(50, 205, 234, 0.9)',
      80: 'rgba(50, 205, 234, 0.8)',
      70: 'rgba(50, 205, 234, 0.7)',
      60: 'rgba(50, 205, 234, 0.6)',
      50: 'rgba(50, 205, 234, 0.5)',
      40: 'rgba(50, 205, 234, 0.4)',
      30: 'rgba(50, 205, 234, 0.3)',
      20: 'rgba(50, 205, 234, 0.2)',
      10: 'rgba(50, 205, 234, 0.1)',
    },
    /**
     * HEX: #3E3B7C
     */
    violet: {
      100: 'rgb(62, 59, 124)',
      80: 'rgba(62, 59, 124, 0.8)',
      60: 'rgba(62, 59, 124, 0.6)',
      40: 'rgba(62, 59, 124, 0.4)',
      20: 'rgba(62, 59, 124, 0.2)',
      10: 'rgba(62, 59, 124, 0.1)',
      5: 'rgba(62, 59, 124, 0.05)',
    },
    /**
     * HEX: #8086F2
     */
    lavender: {
      100: 'rgb(128, 134, 242)',
      10: 'rgba(128, 134, 242, 0.1)',
    },
    sungold: {
      100: '#FDC10A',
    },
    sungoldDarken: {
      100: '#E2A903',
    },
    indigo: {
      100: '#0B132B',
    },
    black: {
      100: '#25282B',
      10: '#E8E8E8',
      5: '#F9F9FA',
    },
    grayDark: {
      100: '#52575C',
    },
    grayMedium: {
      100: '#A0A4A8',
    },
    grayLight: {
      100: '#CACCCF',
    },
    gray5: {
      100: '#C4C4C4',
    },
    gray6: {
      100: '#F2F2F2',
    },
  },
  text: {
    heading: {
      fontFamily: 'robotoSlab',
    },
    h1Bold: {
      fontFamily: 'robotoSlab',
      fontWeight: '700',
      fontSize: [fontSizePosition[36], fontSizePosition[54]],
      lineHeight: [lineHeightPosition[46], lineHeightPosition[64]],
      letterSpacing: 2,
    },
    h1Light: {
      fontFamily: 'lato',
      fontWeight: '300',
      fontSize: [fontSizePosition[34], fontSizePosition[52]],
      lineHeight: [lineHeightPosition[46], lineHeightPosition[64]],
      letterSpacing: 2,
    },
    h2: {
      fontFamily: 'robotoSlab',
      fontWeight: '500',
      fontSize: [fontSizePosition[28], fontSizePosition[48]],
      lineHeight: [lineHeightPosition[38], lineHeightPosition[58]],
      letterSpacing: 0,
    },
    h3: {
      fontFamily: 'lato',
      fontWeight: '700',
      fontSize: [fontSizePosition[24], fontSizePosition[38]],
      lineHeight: [lineHeightPosition[30], lineHeightPosition[42]],
      letterSpacing: 1,
    },
    h4: {
      fontFamily: 'lato',
      fontWeight: '500',
      fontSize: [fontSizePosition[16], fontSizePosition[28]],
      lineHeight: [lineHeightPosition[22], lineHeightPosition[36]],
      letterSpacing: 2,
    },
    h5: {
      fontFamily: 'lato',
      fontWeight: '400',
      fontSize: [fontSizePosition[12], fontSizePosition[20]],
      lineHeight: [lineHeightPosition[18], lineHeightPosition[28]],
      letterSpacing: 2,
    },
    displayH1: {
      variant: 'text.h1Bold',
    },
    displaySubhead: {
      fontFamily: 'robotoSlab',
      fontWeight: '400',
      fontSize: [fontSizePosition[22], fontSizePosition[32]],
      lineHeight: [lineHeightPosition[30], lineHeightPosition[40]],
      letterSpacing: 2,
    },
    heroSubhead: {
      fontFamily: 'lato',
      fontWeight: '400',
      fontSize: [fontSizePosition[24], fontSizePosition[32]],
      lineHeight: [lineHeightPosition[30], lineHeightPosition[36]],
      letterSpacing: 2,
    },
    sidebarH1: {
      fontFamily: 'lato',
      fontWeight: '900',
      fontSize: [fontSizePosition[20], fontSizePosition[30]],
      lineHeight: [lineHeightPosition[26], lineHeightPosition[36]],
      letterSpacing: 2,
    },
    sidebarNumbers: {
      fontFamily: 'lato',
      fontWeight: '900',
      fontSize: [fontSizePosition[20], fontSizePosition[40]],
      lineHeight: [lineHeightPosition[22], lineHeightPosition[42]],
      letterSpacing: 2,
    },
    subtitle: {
      fontFamily: 'lato',
      fontWeight: '700',
      fontSize: [fontSizePosition[8], fontSizePosition[14]],
      lineHeight: [lineHeightPosition[10], lineHeightPosition[18]],
      letterSpacing: 4,
    },
    bodyIntro: {
      fontFamily: 'lato',
      fontWeight: '400',
      fontSize: [fontSizePosition[14], fontSizePosition[20]],
      lineHeight: [lineHeightPosition[22], lineHeightPosition[28]],
      letterSpacing: 2,
    },
    body1: {
      fontFamily: 'lato',
      fontWeight: '400',
      fontSize: [fontSizePosition[16]],
      lineHeight: [lineHeightPosition[24]],
      letterSpacing: 1,
    },
    body2: {
      fontFamily: 'lato',
      fontWeight: '400',
      fontSize: [fontSizePosition[14]],
      lineHeight: [lineHeightPosition[22]],
      letterSpacing: 1,
    },
    byline: {
      fontFamily: 'lato',
      fontWeight: '900',
      fontSize: [fontSizePosition[16]],
      lineHeight: [lineHeightPosition[24]],
      letterSpacing: 1,
    },
    authorName: {
      fontFamily: 'lato',
      fontWeight: '900',
      fontSize: [fontSizePosition[18]],
      lineHeight: [lineHeightPosition[24]],
      letterSpacing: 1,
    },
    captionBold: {
      fontFamily: 'lato',
      fontWeight: '700',
      fontSize: [fontSizePosition[12]],
      lineHeight: [lineHeightPosition[16]],
      letterSpacing: 2,
    },
    caption: {
      fontFamily: 'lato',
      fontWeight: '500',
      fontSize: [fontSizePosition[14]],
      lineHeight: [lineHeightPosition[18]],
      letterSpacing: 2,
    },
    dateline: {
      fontFamily: 'lato',
      fontWeight: '500',
      fontSize: [fontSizePosition[12]],
      lineHeight: [lineHeightPosition[16]],
      letterSpacing: 3,
      textTransform: 'uppercase',
    },
    pullQuote: {
      fontFamily: 'lato',
      fontWeight: '400',
      fontSize: [fontSizePosition[14], fontSizePosition[24]],
      lineHeight: [lineHeightPosition[18], lineHeightPosition[36]],
      letterSpacing: 2,
    },
    button: {
      fontFamily: 'lato',
      fontWeight: '700',
      fontSize: [fontSizePosition[12], null, null, fontSizePosition[14]],
      lineHeight: [lineHeightPosition[16], lineHeightPosition[18]],
      letterSpacing: 2,
      textTransform: 'uppercase',
    },
    cta1: {
      fontFamily: 'robotoSlab',
      fontWeight: '700',
      fontSize: [fontSizePosition[36]],
      lineHeight: [lineHeightPosition[64]],
      letterSpacing: 2,
    },
    cta2: {
      fontFamily: 'robotoSlab',
      fontWeight: '700',
      fontSize: [fontSizePosition[32]],
      lineHeight: [lineHeightPosition[36]],
      letterSpacing: 2,
    },
    dropdown: {
      fontFamily: 'lato',
      fontWeight: '500',
      fontSize: [fontSizePosition[16]],
      lineHeight: [lineHeightPosition[24]],
      letterSpacing: 2,
    },
    autofill: {
      fontFamily: 'lato',
      fontWeight: '500',
      fontSize: [fontSizePosition[18]],
      lineHeight: [lineHeightPosition[32]],
      letterSpacing: 2,
    },
    menu: {
      fontFamily: 'lato',
      fontWeight: '700',
      fontSize: [fontSizePosition[16]],
      lineHeight: [lineHeightPosition[24]],
      letterSpacing: 1,
    },
    copy: {
      fontFamily: 'lato',
      fontWeight: '400',
      fontSize: [fontSizePosition[10], fontSizePosition[16]],
      lineHeight: [lineHeightPosition[24], lineHeightPosition[24]],
      letterSpacing: 1,
    },
    menu2: {
      fontFamily: 'lato',
      fontWeight: '900',
      fontSize: [fontSizePosition[16]],
      lineHeight: [lineHeightPosition[24]],
      letterSpacing: 1,
    },
    menuSubDescription: {
      fontFamily: 'lato',
      fontWeight: '500',
      fontSize: [fontSizePosition[12]],
      lineHeight: [lineHeightPosition[16]], // TODO: Check
      letterSpacing: '0px',
    },
    bulletedList: {
      fontFamily: 'lato',
      fontWeight: '400',
      fontSize: [fontSizePosition[16]],
      lineHeight: [lineHeightPosition[24]],
      letterSpacing: 1,
      textIndent: '24px',
    },
    yellowLabel: {
      backgroundColor: 'sungold.100',
      paddingX: '8px',
      borderRadius: '8px',
      color: '#fff',
    },
    withEllipsisOneLine: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    bigText: {
      fontFamily: 'lato',
      fontWeight: '700',
      fontSize: [fontSizePosition[36], fontSizePosition[96]],
      lineHeight: [lineHeightPosition[104]], // TODO: Check
      letterSpacing: 2,
    },
  },
  backgrounds: {
    gradient1: {
      background: 'linear-gradient(76.62deg, #8086F2 -4.79%, #32CDEA 100%)',
    },
    gradient1Vertical: {
      background: 'linear-gradient(0deg, #8086F2 -4.79%, #32CDEA 100%)',
    },
    gradient2: {
      background:
        'linear-gradient(196.17deg, #8086F2 -1%, #8086F2 -1%, #3E3B7C 100%)',
    },
    gradient3: {
      background:
        'linear-gradient(179.85deg, #3E3B7C 0.51%, #21244E 42.1%, #0B132B 90.47%)',
    },
  },
  styles: {
    p: {
      margin: '0',
    },
  },
  layout: {
    container: {
      width: '100%',
      maxWidth: [
        '100%',
        '990px',
        '1024px',
        '1200px',
        '1366px',
        '1440px',
        '1600px',
      ],
    },
    containerReduced: {
      maxWidth: '1440px',
      width: '100%',
    },
    containerCentered: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
    },
    header: {
      height: ['83px', '192px'],
      position: 'sticky',
      top: 0,
      zIndex: 10,
      width: '100%',
    },
    headerContent: {
      height: '100%',
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.12)',
      backgroundColor: '#fff',
    },
    footer: {
      paddingTop: '59px',
      paddingBottom: ['67px', '168px'],
      variant: 'backgrounds.gradient3',
    },
  },
  breakpoints: ['1024px', '1200px', '1366px', '1440px', '1600px', '1920px'],
  cards: {
    primary: {
      padding: '0',
      borderRadius: '12px',
      // boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
      filter: 'drop-shadow(2px 4px 6px rgba(0, 0, 0,0.2))',
      overflow: 'hidden',
      backgroundColor: 'white',
    },
  },
  transition: {
    duration: '0.3s',
    timingFunction: 'ease-in-out',
  },
});

export default theme;
